// Resolve base typography from the deck stylesheet (TODO §2.4 / §7.4).
//
// The stylesheet is Gridd's `{ selectedSheet, sheets }` format. A sheet holds named
// `presets` (font family/weight/style/size + a color *name*) and a `colors` name→hex
// table — colors are indirected by name so a single sheet swap restyles everything
// (ARCHITECTURE §3). This resolver turns a preset into concrete CSS so SlideCraft's own
// React tree (text blocks now; slide titles/subtitles in §7.4) styles against the same
// source of truth as the grids, rather than hardcoding Tailwind.
//
// It is defensive by design: a fresh deck ships an empty sheet (`sheets: {}`) — the deck's
// own sheet only fills in when the user saves from the stylesheet editor. That gap is
// covered by GRIDD_FACTORY_SHEET, the same factory sheet the Gridd app itself falls back
// to, so an unstyled deck resolves to what its grids are already drawing with instead of
// to a look nobody chose. DEFAULT_TEXT_TYPOGRAPHY below is only the last-ditch guard for a
// sheet that resolves but names no usable preset.

import { GRIDD_FACTORY_SHEET } from "@/lib/gridd-factory-sheet";
import {
  ensureStudioPresets,
  type StudioPresetName,
} from "@/lib/studio-presets";
import type { GriddSheet, GriddStyleSheetData } from "@/types/document";

export interface ResolvedTypography {
  fontFamily?: string;
  // Points, as authored in the stylesheet (§1.4 / §2.6). An "18pt" preset stays 18 here;
  // convert to CSS px at the render boundary with `ptToPx` — never emit this number as px.
  fontSizePt: number;
  color: string;
  fontWeight?: string;
  fontStyle?: string;
}

// Convert a stylesheet point size to CSS px (96dpi: 1pt = 4/3 px). Rendering treats the
// slide as a 96dpi surface, so an 18pt preset paints at 24px — physically 18pt.
export function ptToPx(pt: number): number {
  return Math.round((pt * 96) / 72);
}

// Applied when no stylesheet preset resolves. 18pt renders at 24px, matching today's
// hardcoded slide typography (slate-900 body) so the unstyled default doesn't jump.
export const DEFAULT_TEXT_TYPOGRAPHY: ResolvedTypography = {
  fontSizePt: 18,
  color: "#0f172a", // slate-900
};

// The deck's selected sheet, or — while it has none saved — Gridd's factory sheet, which
// is what the embeds are rendering against anyway. Never undefined: "no stylesheet yet"
// is a state of the *deck*, not of the rendering. Whatever comes out carries SlideCraft's
// own Title/Subtitle/Source presets, seeded from the sheet if it doesn't name them yet, so
// reading resolves exactly what the stylesheet editor offers to edit.
function activeSheet(styleSheet: GriddStyleSheetData): GriddSheet {
  return ensureStudioPresets(
    styleSheet?.sheets?.[styleSheet?.selectedSheet] ?? GRIDD_FACTORY_SHEET,
  );
}

// Turn a preset's fontSize string ("16", "18pt") into a point number — the stylesheet's
// authored unit. parseFloat drops any "pt"/"px" suffix. Non-numeric / empty → fallback.
function parseFontSize(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// Resolve a color that may be a stylesheet color *name* (preferred, indirected) or an
// already-literal value. Unknown names fall through to the raw string so an authored
// hex still works, and finally to the default color.
function resolveColor(
  sheet: GriddSheet | undefined,
  nameOrValue: string | undefined,
  fallback: string,
): string {
  if (!nameOrValue) return fallback;
  const named = sheet?.colors?.find((c) => c.name === nameOrValue);
  return named?.value ?? nameOrValue;
}

type GriddPreset = GriddSheet["presets"][number];

// Turn one resolved preset into concrete CSS. Shared by the base and role resolvers.
function presetToTypography(
  sheet: GriddSheet,
  preset: GriddPreset,
): ResolvedTypography {
  return {
    fontFamily: preset.fontFamily || undefined,
    fontSizePt: parseFontSize(
      preset.fontSize,
      DEFAULT_TEXT_TYPOGRAPHY.fontSizePt,
    ),
    color: resolveColor(sheet, preset.fontColor, DEFAULT_TEXT_TYPOGRAPHY.color),
    fontWeight: preset.fontWeight || undefined,
    fontStyle: preset.fontStyle || undefined,
  };
}

// Resolve typography for a named preset, or (default) the sheet's own default preset.
export function resolveTypography(
  styleSheet: GriddStyleSheetData,
  presetName?: string,
): ResolvedTypography {
  const sheet = activeSheet(styleSheet);

  const name = presetName ?? sheet.defaults?.preset;
  const preset =
    sheet.presets?.find((p) => p.name === name) ?? sheet.presets?.[0];
  if (!preset) return DEFAULT_TEXT_TYPOGRAPHY;

  return presetToTypography(sheet, preset);
}

// === Named roles the slide chrome resolves against (TODO §7.1–7.4) ===
//
// Text blocks want a *body* style. (Title/subtitle/source/sticker are not roles: they resolve
// their own named presets — see resolveStudioTypography.)
//
// §23.0.3 `highlight` used to be here, for the sticker. It is gone: the sticker now has its
// own injected preset like the rest of the slide chrome, nothing else ever resolved the role,
// and a table entry no caller names is the kind of thing that gets adopted by accident later.
// The sheet's own Highlight preset is untouched — it is Gridd's, it is still used by grids,
// and it was simply never ours to borrow.
//
// A sheet may name the body preset anything, or nothing at all, so this is where we *define*
// the role rather than assume the sheet does:
//
//   1. If the active sheet names a preset that plays the role — matched case-insensitively
//      against the candidates below — that preset wins, so an authored sheet drives the
//      look and a sheet swap restyles slide chrome exactly like it restyles grids.
//   2. Otherwise we derive the role from the sheet's *base* typography (its default
//      preset, or the built-in default) and layer role-distinct emphasis on top. Deriving
//      from the base — not a fixed constant — keeps the seam real even with no named
//      preset: recolour the default preset and the sticker follows.
export type TypographyRole = "body" | "smallBody";

// Preset names, lowercased, that each role will adopt if the sheet defines one. First
// match in sheet order wins.
const ROLE_PRESET_CANDIDATES: Record<TypographyRole, string[]> = {
  body: ["body", "body text", "paragraph", "text", "normal", "base"],
  // §26/§6.1 The sheet's secondary running text. Gridd's factory sheet names it `SmallBody`
  // (10pt against Body's 12pt) and every sheet built from it keeps the name, so the lookup is
  // usually exact; the alternatives are for sheets authored by hand.
  smallBody: ["smallbody", "small body", "body small", "small", "secondary"],
};

// Emphasis layered over the base typography when no named preset plays the role.
// `sizeFactor` scales the base font size; `fontWeight`/`color` override when set (a
// role that omits `color` inherits the base colour, so recolouring the base shows).
const ROLE_DEFAULTS: Record<
  TypographyRole,
  { sizeFactor: number; fontWeight?: string; color?: string }
> = {
  // Body is the sheet's plain running text: no emphasis at all, so with no named "Body"
  // preset it resolves to exactly the base typography (what text blocks used before this
  // role existed) and nothing shifts.
  body: { sizeFactor: 1 },
  // A sheet that names no small preset derives one, at the ratio the factory sheet uses
  // between its own two (10pt / 12pt).
  smallBody: { sizeFactor: 10 / 12 },
};

export function resolveRoleTypography(
  styleSheet: GriddStyleSheetData,
  role: TypographyRole,
): ResolvedTypography {
  const sheet = activeSheet(styleSheet);

  // 1. An explicitly named role preset always wins.
  if (sheet.presets?.length) {
    const wanted = ROLE_PRESET_CANDIDATES[role];
    const named = sheet.presets.find((p) =>
      wanted.includes(p.name?.trim().toLowerCase()),
    );
    if (named) return presetToTypography(sheet, named);
  }

  // 2. Otherwise derive from the base typography with role emphasis.
  const base = resolveTypography(styleSheet);
  const emphasis = ROLE_DEFAULTS[role];
  return {
    ...base,
    fontSizePt: Math.round(base.fontSizePt * emphasis.sizeFactor),
    fontWeight: emphasis.fontWeight ?? base.fontWeight,
    color: emphasis.color ?? base.color,
  };
}

// === §23.1 The sticker's tone ===
//
// Three names, never three hexes. Gridd's factory sheet already names `danger`, `warning` and
// `success`, and every sheet built from it keeps them, so red/yellow/green map onto those —
// which means a sheet swap recolours every sticker in the deck for free. That is the whole
// point of the §7.4 seam, and a literal hex here would opt the sticker out of it. The hexes
// below are the last-ditch fallback for a sheet that names none of the three, the same shape
// `resolveColor` already has.
export type StickerTone = "red" | "yellow" | "green";

const TONE_COLOR_NAMES: Record<StickerTone, string> = {
  red: "danger",
  yellow: "warning",
  green: "success",
};

// Only reached by a sheet naming none of the three; Gridd's own factory values.
const TONE_FALLBACK_HEX: Record<StickerTone, string> = {
  red: "#DD0000",
  yellow: "#FFD000",
  green: "#009917",
};

// A colour by NAME from the sheet's table, or the fallback. Deliberately not `resolveColor`:
// that one is for a preset's `fontColor`, where an unknown value is passed through because it
// may already be a literal hex an author typed. Here the input is always one of OUR fixed
// names, so an unknown one means the sheet does not define it — and passing "danger" through
// as a CSS colour silently paints nothing. This is §23.1.2's "literal hexes only as the
// last-ditch fallback", which needs the lookup to actually fail over.
function colorByName(
  sheet: GriddSheet,
  name: string,
  fallback: string,
): string {
  return sheet.colors?.find((c) => c.name === name)?.value ?? fallback;
}

// WCAG relative luminance of an #rgb/#rrggbb colour, 0 (black) to 1 (white). Anything this
// cannot parse reads as black, which lands light text on it — the safer half of the guess,
// since an unparseable value is far more likely to be a deep brand colour than a pastel.
function relativeLuminance(hex: string): number {
  const h = hex.trim().replace(/^#/, "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return 0;
  const channel = (i: number) => {
    const v = parseInt(full.slice(i * 2, i * 2 + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

export interface StickerToneColors {
  background: string;
  color: string;
}

/**
 * §23.1.3 A FILLED pill: background is the tone, text is whichever of the sheet's `text` /
 * `text2` stays readable on it, decided by one luminance test rather than a per-colour table.
 *
 * Two alternatives were rejected for one reason each. Text in the tone colour on a pale pill
 * is unreadable at `warning` — #FFD000 on white fails contrast at any size. A tinted
 * background with body-coloured text reads as decoration rather than as status. The luminance
 * rule then handles a customer's own red, which no table of three could.
 */
export function resolveStickerTone(
  styleSheet: GriddStyleSheetData,
  tone: StickerTone,
): StickerToneColors {
  const sheet = activeSheet(styleSheet);
  const background = colorByName(
    sheet,
    TONE_COLOR_NAMES[tone],
    TONE_FALLBACK_HEX[tone],
  );
  // §23.1.3 asks for a luminance threshold (> 0.55 → dark text). Shipped as a CONTRAST
  // COMPARISON instead — whichever of the sheet's two text colours has the higher WCAG
  // contrast ratio against this background wins — because that is the thing the threshold was
  // approximating, and the approximation breaks on colours the design did not have in front
  // of it. It agrees with §23.1.3 on all three factory tones: #DD0000 (L .15) and #009917
  // (L .23) take the light text, #FFD000 (L .66) the dark one. It differs exactly where the
  // threshold was wrong — a customer's pale red like #FFAAAA lands at L .53, just under the
  // line, and would have been given WHITE text at a contrast ratio of 1.8:1.
  const bg = relativeLuminance(background);
  const dark = colorByName(sheet, "text", "#333333");
  const light = colorByName(sheet, "text2", "#FFFFFF");
  const ratio = (a: number, b: number) =>
    (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  return {
    background,
    color:
      ratio(bg, relativeLuminance(dark)) >= ratio(bg, relativeLuminance(light))
        ? dark
        : light,
  };
}

// §21/§9.2 The sheet's own colour table, name → hex, for a picker to offer as swatches.
// The sticker resolver above looks up ONE known name; a diagram's colour picker offers the
// whole list, because "the colours this deck is made of" is exactly what it is for. Empty
// only for a sheet with no colours at all, which `activeSheet`'s factory fallback prevents.
export function sheetSwatches(
  styleSheet: GriddStyleSheetData,
): { name: string; value: string }[] {
  return activeSheet(styleSheet).colors ?? [];
}

// One colour out of that list, BY NAME. Unknown names resolve to `undefined` rather than to a
// guess: a sheet that dropped a colour should fall back to whatever the caller's default is,
// not paint something nobody chose. This is the counterpart to `resolveColor` above, which
// passes an unknown value through because a preset's `fontColor` may be a literal hex an
// author typed; here the input is always a NAME, so the lookup has to be able to fail.
//
// §26 wrote this for the gantt's bar colours and §15 needed the same three lines for a chart
// series. It lives here rather than in `gantt.ts` so the chart path does not import that
// module — and with it `date-fns` — for a `find`.
export function swatchValue(
  swatches: { name: string; value: string }[],
  name: string | undefined,
): string | undefined {
  if (!name) return undefined;
  return swatches.find((c) => c.name === name)?.value;
}

// Is this colour a grey, a near-black or a near-white — something with no hue to see?
//
// Used to pick a deck's accent and a chart's categorical palette out of the sheet's own colour
// table: a sheet lists its text, border and background colours alongside its brand ones, and
// a palette built from the list in order would draw three shades of grey before it reached
// anything a reader could tell apart. Anything unparseable reads as NOT neutral, so a colour
// this cannot judge is let through rather than silently dropped from a palette.
//
// §26 wrote it for `resolveGanttTheme`'s accent; §15 wants the same judgement per series.
export function isNeutralColor(value: string): boolean {
  const hex = value.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return false;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  // Grey (little spread between channels), or so dark/light that hue is invisible anyway.
  return max - min < 24 || max < 40 || min > 226;
}

// === SlideCraft's own presets (Title / Subtitle / Source / Sticker) ===
//
// Unlike the roles above these need no candidate matching or derived emphasis: we inject
// them ourselves (see @/lib/studio-presets), so `activeSheet` guarantees the sheet names
// them and the lookup is exact. Whatever the stylesheet editor last saved for a preset is
// what paints — the seed only ever covers a sheet that has never been edited.
export function resolveStudioTypography(
  styleSheet: GriddStyleSheetData,
  name: StudioPresetName,
): ResolvedTypography {
  const sheet = activeSheet(styleSheet);
  const preset = sheet.presets?.find((p) => p.name === name);
  return preset ? presetToTypography(sheet, preset) : DEFAULT_TEXT_TYPOGRAPHY;
}
